import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { UnitService, Unit } from '../../../helper/anessa-library/anessa-library/projects/unit-library/src/public-api';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { ResultSetConfigService } from '../../results-helper/result-set-config.service';
import {
  ResultSetConfig,
  ResultSetConfigInterface,
  ResultSetID,
  ResultSetIds
} from '../../../models/results/result-set-config.model';
import Utils from '../../../util';
import {
  ResultSetDropdownId
} from '../../../models/enums/results/result-set-dropdown-ids/result-set-dropdown-config-id.enum';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';
import { LooseObject } from '../../../models/loose-object.model';
import { ResultRow } from '../../../models/data-structures/results/result-row.model';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { EAFM } from '../../../models/results/financial/eafm/eafm.model';

@Component({
  selector: 'app-baseline-financial-metrics',
  templateUrl: './baseline-financial-metrics.component.html',
  styleUrls: ['./baseline-financial-metrics.component.scss']
})
export class BaselineFinancialMetricsComponent extends UserpilotReloader implements OnInit, OnDestroy, AnessaTranslationInterface {
  onDestroyNotifier = new EventEmitter();

  dataLoadedNotifier = new Subject<boolean>();

  /** Currency exchange rates list */
  currencyData: Unit[] = [];
  /** Date the exchange rates are from */
  exchangeRateDate = null;
  /** Selected Currency */
  selectedCurrency: Unit;


  /** List of results */
  resultOptions: string[] = [];

  /** Table data */
  tableData?: LooseObject;

  /** Custom headers */
  tableHeaders?: string[];

  /** List of selected results */
  selectedResults: any[] = [];

  /** Financial section of the results */
  metricResults?: LooseObject;

  trigger?: string;
  labelHeader?: string;
  valueHeader?: string;
  unitHeader?: string;

  projectCommServiceReady = false;
  unitServiceReady = false;
  resultsLoaded = false;
  exchangeRatesLoaded = false;

  resultConf?: ResultSetConfig;
  PROJECT_ID: string | null = '';

  constructor(private projectCommService: ProjectCommunicationService,
              private toastrService: ConfiguredToastrService,
              private unitService: UnitService,
              private translationService: AnessaTranslationService,
              private resultSetConfigService: ResultSetConfigService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setTranslations();
     this.projectCommService.projectFullyLoadedNotifier
       .pipe(takeUntil(this.onDestroyNotifier))
       .subscribe((ready: boolean) => {
         this.projectCommServiceReady = ready;
         if (ready) {
           this.PROJECT_ID = this.projectCommService.getProjectId();
           this.setup();
         }
       })
    this.projectCommService.resultsLoadedNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((reloaded: boolean) => {
       this.resultsLoaded = reloaded;
       if (reloaded) {
         this.setupResults();
       }
      });

     this.unitService.unitsLoadedNotifier
       .pipe(takeUntil(this.onDestroyNotifier))
       .subscribe((ready: boolean) => {
         this.unitServiceReady = ready;
         if (ready) {
           this.setupResults();
         }
       });
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.setTranslations();
        this.setTableData();
        this.trigger = language;
      });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  setup() {
    if (this.servicesReady()) {
      // default selection
      if (this.currencyData) {
        // set default currency
        if (!this.selectedCurrency && this.projectCommService.getProjectInfo().defaults.currency) {
          this.selectedCurrency = this.currencyData.find(curr => curr.id === this.projectCommService.getProjectInfo().defaults.currency);
        }
        if (!this.selectedCurrency) {
          this.selectedCurrency = this.currencyData.find(curr => curr.id === '000000000000000000002004'); // CAD
        } else {
          this.selectedCurrency = this.currencyData.find(curr => curr.id === this.selectedCurrency.id);
        }
      }
      if (this.selectedResults.length === 0) {
        this.inputsCheckAll({checked: true});
      }
      // process result conf (sets table data as well)
      this.fetchResultSetConfigData();
    }
  }

  servicesReady() {
    return this.resultsLoaded && this.unitServiceReady &&
      this.exchangeRatesLoaded && this.projectCommServiceReady;
  }

  setTranslations() {
    // label is not included in the table headers array, just used for obj creation
    this.labelHeader = this.translationService.get('RESULTS.DATA.LABEL');
    this.valueHeader = this.translationService.get('RESULTS.DATA.VALUES');
    this.unitHeader = this.translationService.get('RESULTS.DATA.UNIT');
    this.tableHeaders = [
      this.valueHeader,
      this.unitHeader
    ];
    if (this.currencyData) {
      this.setTableData();
    }
  }

  /**
   * Get the currency exchange rates
   * @param created date of exchange rates to get
   */
  getFinExchange(created: string | null) {
    this.unitService.getAllCurrencyUnits(created).then(
      (units: Unit[]) => {
        this.exchangeRateDate = this.unitService.getExchangeRateDate();
        this.currencyData = units.filter(unit => {
          return unit.unitType === '000000000000000000000020';
        });

        // sort rates alphabetically
        this.currencyData.sort((first: Unit, second: Unit) => {
          if (Number(first.order) < Number(second.order)) {
            return -1;
          } else if (Number(second.order) < Number(first.order)) {
            return 1;
          } else {
            return 0;
          }
        });
        this.exchangeRatesLoaded = true;
        this.setup();
      }
    );
  }

  /**
   * Setup function for the result select.
   */
  setupResults(): void {
    if (!this.resultsLoaded || !this.unitServiceReady) return;
    const results = this.projectCommService.getResults();
    if (results && results.financial) {
      this.metricResults = results.financial.eafm;

      // we want to hide `Leverage Ratios` and `Coverage Ratios` if there are no non-forgiven loans.
      const nonForgivenLoans = this.projectCommService.getNonForgivenLoans();
      if(!nonForgivenLoans || nonForgivenLoans?.length < 1) {
        const keys = ['RESULTS.DATA.ECONOMIC_METRICS.CR', 'RESULTS.DATA.ECONOMIC_METRICS.LR'] as (keyof EAFM)[];
        for (const key of keys) {
          if (key in this.metricResults) delete this.metricResults[key];
        }
      }

      this.getFinExchange(results.created);
      this.setResultSelectOptions();
    } else {
      this.toastrService.warning(
        this.translationService.get('RESULTS.ERRORS.PLEASE_SIMULATE'),
        this.translationService.get('RESULTS.ERRORS.NO_RESULTS')
      );
    }
  }

  /**
   * Creates custom options used for the result select
   */
  setResultSelectOptions(): void {
    this.resultOptions = [];
    if (!this.metricResults) return;
    for (const section of Object.keys(this.metricResults)) {
      this.resultOptions.push(section);
    }
  }

  /**
   * Updates the selection status of the specified result
   * @param result result clicked
   * @param event checkbox checked event
   */
  optClick(result: string): void {
    if (!this.selectedResults.includes(result)) {
      this.selectedResults.push(result);
    } else if (this.selectedResults.includes(result)) {
      this.selectedResults.splice(this.selectedResults.indexOf(result), 1);
    }
    this.setTableData();
    this.selectedResults = [...this.selectedResults];
  }

  /**
   * Changes status of all options to selected or unselected
   * @param $event click event
   */
  inputsCheckAll($event: Partial<MatCheckboxChange>): void {
    if ($event.checked) {
      this.selectedResults = [...this.resultOptions];
    } else {
      this.selectedResults = [];
    }
    this.setTableData();
    setTimeout(() => {
      this.selectedResults = [...this.selectedResults];
    });
  }

  /**
   * Checks if all options are selected
   */
  allInputsSelected(): boolean {
    for (const item of this.resultOptions) {
      if (!this.selectedResults.includes(item)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Checks if at least one option is selected
   */
  isCheckAllIndeterminate(): boolean {
    let atLeastOneIncluded = false;
    let allIncluded = true;
    for (const item of this.resultOptions) {
      if (!this.selectedResults.includes(item)) {
        atLeastOneIncluded = true;
      } else {
        allIncluded = false;
      }
    }

    return atLeastOneIncluded && !allIncluded;
  }

  /**
   * Sets the data to be dislayed
   */
  setTableData(): void {
    this.tableData = undefined;
    if (!this.selectedResults || !this.selectedResults.length || !this.metricResults) return;
    let tempTableData = this.metricResults;
    tempTableData = Utils.deepCopy(tempTableData)

    for (const result of Object.keys(tempTableData)) {
      if (!this.selectedResults.find(res => res === result)) {
        delete tempTableData[result as keyof EAFM];
      } else {
        if (result === 'RESULTS.DATA.PARTNERSHIP') {
          tempTableData = {...tempTableData, ...tempTableData['RESULTS.DATA.PARTNERSHIP']};
          delete tempTableData['RESULTS.DATA.PARTNERSHIP'];
        }
      }
    }
    this.convertValuesUnits(tempTableData);
    this.tableData = tempTableData;
  }

  /**
   * Checks if at least one result is selected
   */
  isShowResults(): boolean {
    if (this.selectedResults.length > 0) {
      return true;
    }
    return false;
  }

  convertValuesUnits(result: LooseObject) {
    if (!this.selectedCurrency) return;
    if (result instanceof ResultRow) {
      const values = result.values;
      const originalUnitId = result.unit;

      if (originalUnitId && values && values[0] !== null && values[0] !== undefined) {
        // CAD or CAD/year
        if (originalUnitId === '000000000000000000002004' || originalUnitId === '000000000000000000002205') {
            // already in cad so this is fine
            values[0] = this.selectedCurrency.convertFromBase(values[0]);
            result.unit = this.getUnitDisplayNameForSelectedCur(originalUnitId); // already the base (CAD)
        }  else {
          // check if unit is currency, and convert it from its current unit to target unit
          // if percentage units, skip
          if (!['0000000000000000000002101', '0000000000000000000002201'].includes(originalUnitId)) {
            const originalUnit = this.unitService.getUnitById(originalUnitId);
            if (!originalUnit) throw Error('unit not found with the the id found in the results');

            const currencyUnitTypeIds = [
              '000000000000000000000020', '000000000000000000000021', '000000000000000000000022'
            ];
            // if unit is a currency unit type
            if (currencyUnitTypeIds.includes(originalUnit.unitType)) {
              // after currencies are loaded this is getting the conversion factors
              const baseValue = originalUnit.convertToBase(values[0]);
              values[0] = this.selectedCurrency.convertFromBase(baseValue);
              result.unit = this.getUnitDisplayNameForSelectedCur(this.getBaseCurUnitForType(originalUnit.unitType));
            }
          } else {
            result.unit = this.getUnitDisplayNameForSelectedCur(originalUnitId);
          }
        }
      }
    } else {
      for (const subResult of Object.keys(result)) {
        this.convertValuesUnits(result[subResult]);
      }
    }
  }

  getBaseCurUnitForType(unitType: string): string {
    switch (unitType) {
      case '000000000000000000000020':
        return '000000000000000000002004';
      case '000000000000000000000021':
        return '000000000000000000002106';
      case '000000000000000000000022':
        return '000000000000000000002205';
      default:
        throw Error('cannot get base cur unit for ' + unitType);
    }
  }

  /**
   * Returns an altered display name based on the selected currency. Unit conversion not performed here, just unit
   * display name alteration.
   * ex. if USD is selected, the base CAD/year unit will return USD/year.
   * @param baseUnitId
   */
  getUnitDisplayNameForSelectedCur(baseUnitId: string): any {
    let alteredUnitDisplayName = '';
    let baseUnit: Unit = null;

    if (baseUnitId) {
      baseUnit = this.unitService.getUnitById(baseUnitId);
      if (baseUnit) {
        const baseUnitDisplayName = baseUnit.displayName;
        if (this.selectedCurrency != null) {
          alteredUnitDisplayName = baseUnitDisplayName.replace(/CAD/g, this.selectedCurrency.displayName);
        }
      } else {
        alteredUnitDisplayName = 'n/a';
      }
    }
    return alteredUnitDisplayName;
  }


  private fetchResultSetConfigData() {
    if (!this.PROJECT_ID) return;
    if (this.projectCommServiceReady && this.resultsLoaded) {
      this.resultSetConfigService.fetch(
        [ResultSetID.FINANCIAL_METRICS],
        this.PROJECT_ID
      )
        .pipe(take(1))
        .pipe(takeUntil(this.onDestroyNotifier))
        .subscribe(res => {
          this.setupResultSetConfigs(res);
          this.setTableData();
        });
    }
  }

  private setupResultSetConfigs(res: ResultSetIds): void {
    if (!res && !res['obj']) {
      throw Error('invalid response from result set config, unable to process');
    }
    const processedObj = Utils.restoreMapsInJson(res['obj'], 'dropdownDefaults');
    const resultSetConfInterface: ResultSetConfigInterface = processedObj[ResultSetID.FINANCIAL_METRICS];
    if (resultSetConfInterface) {
      this.resultConf = new ResultSetConfig(resultSetConfInterface);
    } else {
      this.resultConf = new ResultSetConfig();
      this.resultConf.resultSetID = ResultSetID.FINANCIAL_METRICS;
    }
    this.processResultSetDefaults();
  }

  processResultSetDefaults(): void {
    if (!this.projectCommServiceReady || !this.resultsLoaded) return;
    if (!this.resultConf || !this.resultConf.dropdownConfigEnabled) return;
    const defaults = this.resultConf.dropdownDefaults;
    if (defaults.has(ResultSetDropdownId.FINANCIAL_METRICS_RES)) {
      this.selectedResults = defaults.get(ResultSetDropdownId.FINANCIAL_METRICS_RES);
    }
    if (defaults.has(ResultSetDropdownId.FINANCIAL_METRICS_CURR)) {
      this.selectedCurrency = this.currencyData.find(
        cur => cur.id === defaults.get(ResultSetDropdownId.FINANCIAL_METRICS_CURR)
      );
    }
  }

  onResultSetDefaultChange() {
    if (!this.resultConf || !this.PROJECT_ID) return;
    if (this.resultConf?.dropdownConfigEnabled && Utils.isMap(this.resultConf.dropdownDefaults)) {
      this.resultConf.dropdownDefaults.set(ResultSetDropdownId.FINANCIAL_METRICS_RES, this.selectedResults);
      this.resultConf.dropdownDefaults.set(ResultSetDropdownId.FINANCIAL_METRICS_CURR, this.selectedCurrency?.id);
    }
    this.resultSetConfigService.save(this.resultConf, this.PROJECT_ID);
  }

  setResultSetConfDisabled() {
    if (this.resultConf?.dropdownConfigEnabled && this.PROJECT_ID) {
      this.resultConf.dropdownConfigEnabled = false;
      this.resultSetConfigService.save(this.resultConf, this.PROJECT_ID);
    }
  }
}
